import { useState, useEffect, useRef } from 'react';
import { TutorialOverlay } from './TutorialOverlay';
import { useTutorialSettings } from '@/hooks/useTutorialSettings';

interface GameTutorialProps {
  gameId: number;
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface TutorialStep {
  targetSelector: string;
  title: string;
  message: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function GameTutorial({ gameId, isVisible, onComplete, onSkip }: GameTutorialProps) {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [tutorialReady, setTutorialReady] = useState<boolean>(false);
  const { markTutorialComplete, disableTutorial } = useTutorialSettings();
  
  // Use a state to track device size for responsive tutorial positioning
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);

  // Detect mobile devices on component mount
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 640;
      setIsMobileDevice(mobile);
    };
    
    // Check immediately
    checkIfMobile();
    
    // Listen for window resize
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Define tutorial steps with device-responsive positions
  const getMobileFriendlyPosition = (desktopPosition: 'top' | 'bottom' | 'left' | 'right'): 'top' | 'bottom' | 'left' | 'right' => {
    if (!isMobileDevice) return desktopPosition;
    
    // On mobile, simplify positions to top/bottom for better visibility
    if (desktopPosition === 'left' || desktopPosition === 'right') {
      return 'bottom';
    }
    return desktopPosition;
  };
  
  // Define tutorial steps - with mobile-friendly shorter content
  const tutorialSteps: TutorialStep[] = [
    {
      targetSelector: '#game-container',
      title: 'Welcome to Lead Card!',
      message: 'Learn the basics of the game in this quick tutorial.',
      position: getMobileFriendlyPosition('top')
    },
    {
      targetSelector: '.player-hand', 
      title: 'Your Cards',
      message: 'These are your cards. Tap or click on a card to play it.',
      position: getMobileFriendlyPosition('top')
    },
    {
      targetSelector: '.player-hand .card',
      title: 'Card Values',
      message: 'Cards have suits (♠♥♣♦) and ranks (6-K). Higher ranks beat lower ranks of the same suit.',
      position: getMobileFriendlyPosition('top')
    },
    {
      targetSelector: '.game-board',
      title: 'Game Board',
      message: 'Played cards appear here. First player can play any card. Second player must follow suit if possible.',
      position: getMobileFriendlyPosition('bottom')
    },
    {
      targetSelector: '.game-board',
      title: 'Winning Rounds',
      message: 'Higher card of the same suit wins. If player can\'t follow suit, the leading player wins automatically.',
      position: getMobileFriendlyPosition('top')
    },
    {
      targetSelector: '.game-board',
      title: 'Scoring',
      message: '6 cards = 3 points, 7 cards = 2 points, 8-K = 1 point each.',
      position: getMobileFriendlyPosition('right')
    },
    {
      targetSelector: '.game-board',
      title: 'Winning',
      message: 'First to 10 points wins the match. Good luck!',
      position: getMobileFriendlyPosition('bottom')
    }
  ];
  
  // Check if all DOM elements needed for the tutorial are ready
  useEffect(() => {
    if (!isVisible) return;
    
    let checkAttemptsCount = 0;
    const maxCheckAttempts = 15; // Increased max attempts for better reliability
    let intervalRef: NodeJS.Timeout | null = null;
    
    // Function to check if elements exist
    const checkElements = () => {
      console.log(`Checking for tutorial elements (attempt ${checkAttemptsCount + 1}/${maxCheckAttempts})...`);
      
      // Wait for the game to be fully loaded before checking elements
      if (!document.querySelector('#game-container')) {
        console.log('Game container not yet ready, waiting...');
        checkAttemptsCount++;
        return false;
      }
      
      // Check for critical game elements
      const gameContainerExists = !!document.querySelector('#game-container');
      const playerHandExists = !!document.querySelector('.player-hand');
      const gameboardExists = !!document.querySelector('.game-board');
      
      // Log the current DOM state for debugging
      console.log('Current game DOM state:', {
        gameContainer: gameContainerExists,
        playerHand: playerHandExists,
        gameBoard: gameboardExists,
        playerCards: document.querySelectorAll('.player-hand .card').length,
        bodyContent: document.body.innerHTML.substring(0, 100) + '...' // Log a snippet of body content
      });
      
      // If we don't have critical game UI elements yet, wait for them
      if (!gameContainerExists || !playerHandExists || !gameboardExists) {
        console.log('Critical game elements missing, waiting...');
        checkAttemptsCount++;
        return false;
      }
      
      // For the first tutorial step, we only need the game container
      if (currentStep === 0) {
        console.log('First tutorial step only needs game container, proceeding...');
        setTutorialReady(true);
        return true;
      }
      
      // For other steps that need specific elements, make sure they exist
      // Create a list of selectors needed for the current step
      const currentSelector = tutorialSteps[currentStep]?.targetSelector;
      const currentElementExists = !!document.querySelector(currentSelector);
      
      if (!currentElementExists) {
        console.log(`Current step element missing: ${currentSelector}`);
        
        // If we've reached max attempts, use what we have
        if (checkAttemptsCount >= maxCheckAttempts) {
          console.log('Max check attempts reached, starting tutorial anyway with available elements');
          setTutorialReady(true);
          return true;
        }
        
        checkAttemptsCount++;
        return false;
      }
      
      // We have the necessary elements for at least the first step
      console.log('Tutorial elements found, ready to start tutorial!');
      setTutorialReady(true);
      return true;
    };
    
    // Initial delay before first check - give the game time to load
    // Increased delay to ensure the game is fully rendered
    const initialTimeout = setTimeout(() => {
      // If initial check succeeds, we're done
      if (checkElements()) return;
      
      // Otherwise, set up interval to keep checking
      intervalRef = setInterval(() => {
        if (checkElements()) {
          if (intervalRef) clearInterval(intervalRef);
        }
      }, 1000);
      
    }, 3000); // Longer initial wait to ensure game is fully loaded
    
    // Clean up function
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef) clearInterval(intervalRef);
    };
  }, [isVisible, tutorialSteps, currentStep]);
  
  // Clean up any highlighted elements
  const cleanupHighlightedElements = () => {
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });
  };
  
  // Move to the next step
  const handleNext = () => {
    console.log("Next button clicked. Current step:", currentStep, "of", tutorialSteps.length - 1);
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(prevStep => prevStep + 1);
      console.log("Moving to next step:", currentStep + 1);
    } else {
      // Tutorial completed
      console.log("Tutorial completed");
      cleanupHighlightedElements();
      
      // Mark tutorial as completed in localStorage
      markTutorialComplete('game-basics');
      
      // Notify parent component that tutorial is completed
      if (onComplete) onComplete();
    }
  };
  
  // Skip the tutorial and mark it as completed
  const handleSkip = () => {
    console.log("Tutorial skipped");
    cleanupHighlightedElements();
    
    // Mark tutorial as completed and disabled in localStorage
    markTutorialComplete('game-basics');
    disableTutorial('game-basics');
    
    // Notify parent component that tutorial is skipped
    if (onSkip) onSkip();
  };
  
  // Track touch gestures for swipe navigation on mobile
  const touchStartRef = useRef<number | null>(null);
  
  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobileDevice) {
      touchStartRef.current = e.touches[0].clientX;
    }
  };
  
  // Handle touch end to detect swipes
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobileDevice || touchStartRef.current === null) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStartRef.current - touchEnd;
    
    // If swipe distance is significant (more than 50px)
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swipe left, go to next step
        if (currentStep < tutorialSteps.length - 1) {
          handleNext();
        }
      } else {
        // Swipe right, go to previous step (if not first step)
        if (currentStep > 0) {
          setCurrentStep(prevStep => prevStep - 1);
        }
      }
    }
    
    touchStartRef.current = null;
  };
  
  // Don't render until the game is ready
  if (!isVisible || !tutorialReady) return null;
  
  const currentTutorialStep = tutorialSteps[currentStep];
  
  return (
    <div 
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="w-full h-full absolute inset-0 z-[9998] pointer-events-none"
    >
      <TutorialOverlay
        targetSelector={currentTutorialStep.targetSelector}
        title={currentTutorialStep.title}
        message={currentTutorialStep.message}
        position={currentTutorialStep.position}
        onNext={handleNext}
        onSkip={handleSkip}
        isVisible={isVisible}
        currentStep={currentStep}
        totalSteps={tutorialSteps.length}
      />
    </div>
  );
}